"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  type CreateIngredientFormData,
  convertFormDataToIngredientPayload,
  getDefaultIngredientFormValues,
} from "../create-ingredient/forms/schema";
import { useIngredientForm } from "../create-ingredient/useIngredientForm";
import { AdvancedForm } from "../create-ingredient/forms/advanced";
import { updateIngredient } from "@/app/actions/ingredients/updateIngredient";
import { Ingredient } from "@/lib/types";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { roundToNearest, toNumber } from "@/lib/utils";

interface EditIngredientDialogProps {
  ingredient: Ingredient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditIngredientDialog({
  ingredient,
  open,
  onOpenChange,
  onSuccess,
}: EditIngredientDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useIngredientForm();

  const onSubmit = async (data: CreateIngredientFormData) => {
    setIsSubmitting(true);
    try {
      const updateData = convertFormDataToIngredientPayload(data);

      await updateIngredient({
        data: { ingredientId: ingredient.id, updates: updateData },
      });
      toast.success("Ingredient updated successfully");
      trackEvent("my-ingredients-edit-ingredient-success");
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to update ingredient");
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when dialog opens or ingredient changes
  useEffect(() => {
    if (open) {
      const formatPct = (pct: string) =>
        roundToNearest(parseFloat(pct) * 100, 0.01);

      form.reset(
        getDefaultIngredientFormValues({
          name: ingredient.name,
          notes: ingredient.notes || "",
          fatPct: formatPct(ingredient.fatPct),
          saturatedFatPct: formatPct(ingredient.saturatedFatPct),
          transFatPct: formatPct(ingredient.transFatPct),
          sugarsPct: formatPct(ingredient.sugarsPct),
          lactosePct: formatPct(ingredient.lactosePct),
          waterPct: formatPct(ingredient.waterPct),
          proteinPct: formatPct(ingredient.proteinPct),
          carbPct: formatPct(ingredient.carbPct),
          fiberPct: formatPct(ingredient.fiberPct),
          alcoholPct: formatPct(ingredient.alcoholPct),
          cocoaSolidsNonfatPct: formatPct(ingredient.cocoaSolidsNonfatPct),
          cocoaFatPct: formatPct(ingredient.cocoaFatPct),
          emulsifierPct: formatPct(ingredient.emulsifierPct),
          stabilizerPct: formatPct(ingredient.stabilizerPct),
          pod: formatPct(ingredient.pod),
          pac: formatPct(ingredient.pac),
          energyKcal: toNumber(ingredient.energyKcal),
        }),
      );
    }
  }, [open, ingredient, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit ingredient</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            id="edit-ingredient-form"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AdvancedForm form={form} selectedCategory={ingredient.category} />
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" form="edit-ingredient-form" busy={isSubmitting}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
